import { TCallbackFunction } from "../../../interfaces/common"
import { IUserResponse } from "../../../interfaces/models/user"
import { IAuthData } from "../../../interfaces/store/authStore"
import { useAuthStore } from "../../../stores/auth/useAuthStore"
import { IResponse, useAxios } from "../useAxios"

export const useAuthRequest = () => {
    const { sendRequest } = useAxios()
    const { store: { authData: { accessToken } } } = useAuthStore()

    const route = '/auth'

    const getUser = (params?: IAuthData, slug: string = ""): Promise<IUserResponse> => {
        return sendRequest({
            enabled: params ? !!params.accessToken : !!accessToken,
            slug: slug || 'user',
            url: `${route}/me`,
            method: 'GET',
            ...params && { additionalHeaders: { ...params } }
        })
    };

    const verificationCode = async (body: any) => {
        const response = await sendRequest({
            slug: 'verification-code',
            url: `${route}/verification-code`,
            method: 'POST',
            payload: body,
            all: true
        });

        return response;
    };

    const verificationCodeValidate = async (body: any) => {
        const response = await sendRequest({
            slug: 'verification-code-validate',
            url: `${route}/verification-code/validate`,
            method: 'POST',
            payload: body,
            all: true
        });

        return response;
    };

    const resetPassword = async (body: any) => {
        const response = await sendRequest({
            slug: 'reset-password',
            url: `${route}/reset-password`,
            method: 'POST',
            payload: body,
            all: true
        });

        return response;
    };

    const invitePublic = async (inviteCode: string) => {
        const response = await sendRequest({
            slug: 'invite-public',
            url: `/invites/code/${inviteCode}`,
            displayErrorToast: false,
            all: true,
        });

        if (!response) return undefined;
        return response.data ?? response;
    };

    const signInEmail = async (body: any) => {
        const response = await sendRequest({
            slug: 'login-email',
            url: `${route}/login`,
            method: 'POST',
            payload: body,
            displayErrorToast: false,
            displayToast: false,
            all: true,
        });

        return response;
    };

    const signInGoogle = async (body: any) => {
        const response = await sendRequest({
            slug: 'login-google',
            url: `${route}/login/google`,
            method: 'POST',
            payload: body,
            displayErrorToast: true,
            displayToast: false
        });

        return response;
    };


    const preRegisterEmail = async (body: any, other?: any) => {
        const response = await sendRequest({
            slug: 'pre-register',
            url: `${route}/pre-register`,
            method: 'POST',
            payload: body,
            displayErrorToast: false,
            displayToast: false,
            ...other,
            all: true
        });

        return response;
    };

    const resendVerificationEmail = async (body: { email: string }) => {
        const response = await sendRequest({
            slug: 'resend-verification-email',
            url: `${route}/resend-verification-email`,
            method: 'POST',
            payload: body,
            displayErrorToast: false,
            displayToast: false,
            all: true,
        });

        return response;
    };

    const checkEmail = async (email: string) => {
        const response = await sendRequest({
            slug: 'check-email',
            url: `${route}/check-email`,
            method: 'POST',
            payload: { email },
            displayErrorToast: false,
            displayToast: false,
            all: true,
        });

        return response;
    };

    const verifyEmail = async (token: string) => {
        const response = await sendRequest({
            slug: 'verify-email',
            url: `${route}/verify-email?token=${token}`,
            method: 'POST',
            displayErrorToast: false,
            displayToast: false,
            all: true,
        });

        return response;
    };

    const sendDesktopLink = async (body: { source?: 'home' | 'onboarding' }) => {
        const response = await sendRequest({
            slug: 'send-desktop-link',
            url: `${route}/send-desktop-link`,
            method: 'POST',
            payload: body,
            displayErrorToast: false,
            displayToast: false,
            all: true,
        });

        return response;
    };

    const signUpEmail = async (body: any, other?: any) => {
        const response = await sendRequest({
            slug: 'register-email',
            url: `${route}/register`,
            method: 'POST',
            payload: body,
            displayErrorToast: true,
            displayToast: false,
            ...other
        });

        return response;
    };

    const signUpGoogle = async (body: any, other?: any) => {
        const response = await sendRequest({
            slug: 'register-google',
            url: `${route}/register/google`,
            method: 'POST',
            payload: body,
            displayErrorToast: true,
            displayToast: false,
            ...other
        });

        return response;
    };


    const switchUserWorkspace = async (payload: any, additionalHeaders?: {[key: string]: string; } | undefined) => {
        const response = await sendRequest({
            slug: 'switch-user-workspace',
            url: `${route}/me/context`,
            method: 'PUT',
            payload,
            displayToast: false,
            additionalHeaders
        });

        return response;
    };

    const generateAPIKey = async () => {
        const response = await sendRequest({
            slug: 'generate-api-key',
            url: `${route}/api-key`,
            method: 'POST',
            displayToast: false
        });

        return response;
    };

    const revokeAPIKey = async () => {
        const response = await sendRequest({
            slug: 'revoke-api-key',
            url: `${route}/api-key`,
            method: 'DELETE',
            displayToast: false
        });

        return response;
    };

    const getAPIKeys = async () => {
        const response = await sendRequest({
            slug: 'get-api-keys',
            url: `${route}/api-keys`,
            method: 'GET',
            displayToast: false,
            displayErrorToast: true
        });

        return response;
    };

    const generateMemberAPIKey = async () => {
        const response = await sendRequest({
            slug: 'generate-member-api-key',
            url: `${route}/api-keys/members`,
            method: 'POST',
            displayToast: false,
            all: true
        });

        return response;
    };

    const revokeMemberAPIKey = async (userId: string) => {
        const response = await sendRequest({
            slug: 'revoke-member-api-key',
            url: `${route}/api-keys/members/${userId}/revoke`,
            method: 'POST',
            displayToast: false,
            all: true
        });

        return response;
    };

    const deleteMemberAPIKey = async (userId: string) => {
        const response = await sendRequest({
            slug: 'delete-member-api-key',
            url: `${route}/api-keys/members/${userId}`,
            method: 'DELETE',
            displayToast: false,
            all: true
        });

        return response;
    };

    const logout = async () => {
        const response = await sendRequest({
            slug: 'logout',
            url: `${route}/logout`,
            method: 'POST',
            displayToast: false,
            displayErrorToast: false
        });

        return response;
    };

    // WorkOS SSO methods
    const workOSLogoutUrl = async (sessionId?: string) => {
        const response = await sendRequest({
            slug: 'workos-logout-url',
            url: `${route}/workos/logout-url`,
            method: 'GET',
            params: sessionId ? { sessionId } : undefined,
            displayToast: false,
            displayErrorToast: false
        });

        return response;
    };
    const workOSLookupOrganization = async (body: { domain?: string; email?: string }) => {
        const response = await sendRequest({
            slug: 'workos-lookup',
            url: `${route}/workos/lookup`,
            method: 'POST',
            payload: body,
            displayErrorToast: true,
            displayToast: false,
            all: true
        });

        return response;
    };

    const workOSGetAuthorizationUrl = async (params: { organization?: string; domain?: string; email?: string }) => {
        const response = await sendRequest({
            slug: 'workos-sso',
            url: `${route}/workos/sso`,
            method: 'GET',
            params,
            displayErrorToast: true,
            displayToast: false
        });
        console.log('workOSGetAuthorizationUrl response', response);
        return response;
    };

    const workOSCallback = async (body: { code: string; email?: string }, inviteCode?: string) => {
        const response = await sendRequest({
            slug: 'workos-callback',
            url: `${route}/workos/callback`,
            method: 'POST',
            payload: {
                ...body,
                ...(inviteCode && { inviteCode })
            },
            displayErrorToast: true,
            displayToast: false
        });

        return response;
    };

    return {
        getUser,
        verificationCode,
        verificationCodeValidate,
        resetPassword,
        invitePublic,
        signInEmail,
        signInGoogle,
        preRegisterEmail,
        resendVerificationEmail,
        checkEmail,
        signUpEmail,
        signUpGoogle,
        switchUserWorkspace,
        generateAPIKey,
        revokeAPIKey,
        getAPIKeys,
        generateMemberAPIKey,
        revokeMemberAPIKey,
        deleteMemberAPIKey,
        verifyEmail,
        sendDesktopLink,
        logout,
        workOSLookupOrganization,
        workOSGetAuthorizationUrl,
        workOSCallback,
        workOSLogoutUrl
    };
}